import { isLoggedIn } from '@/api/api.auth';
import { type UserGroup } from '@/modules/Permissions/Permissions.constants';
import { prepareSlug } from '@/utils/string.utils';
import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useEffect, useState } from 'react';
import secureLocalStorage from 'react-secure-storage';

export type AuthenticationStatus = 'LOADING' | 'AUTHENTICATED' | 'UNAUTHENTICATED' | 'RESET';

type AuthContextType = {
  authenticationStatus: AuthenticationStatus;
  setAuthenticationStatus: Dispatch<SetStateAction<AuthenticationStatus>>;
  userGroups: UserGroup[] | undefined;
  setUserGroups: Dispatch<SetStateAction<UserGroup[]>>;
  resetAuth: () => void;
};

const initialContext: AuthContextType = {
  authenticationStatus: 'LOADING',
  setAuthenticationStatus: () => {},
  userGroups: undefined,
  setUserGroups: () => {},
  resetAuth: () => {},
};

export const AuthContext = createContext<AuthContextType>(initialContext);
export const useAuthContext = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [authenticationStatus, setAuthenticationStatus] = useState<AuthenticationStatus>('LOADING');
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]);

  const resetAuth = useCallback(() => {
    setAuthenticationStatus('RESET');
    setUserGroups([]);
  }, []);

  useEffect(() => {
    try {
      const isUserLoggedIn = isLoggedIn();
      setAuthenticationStatus(isUserLoggedIn ? 'AUTHENTICATED' : 'UNAUTHENTICATED');
      const groups = secureLocalStorage.getItem('groups') as UserGroup[] | undefined;
      if (groups) {
        const preparedGroups = groups.map((group: UserGroup) => prepareSlug(group));
        setUserGroups(preparedGroups);
      }
    } catch {
      setAuthenticationStatus('LOADING');
    }
  }, []);

  return (
    <AuthContext.Provider value={{ authenticationStatus, setAuthenticationStatus, userGroups, setUserGroups, resetAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
